<template>
  <div>
    <wc-page-title title='Create Journal Entry'>
      <template v-slot:rightSide>
        <wc-posted :posted='false'></wc-posted>
      </template>

    </wc-page-title>
    <v-container v-if='journal_entry'>
      <v-row>
        <v-col cols='12' md='6'>
          <wc-data-box
            title='Description'
            :showEdit='true'
            @edit='edit'
          >
            <v-card-text>
              <span>{{ journal_entry.description }}</span>
            </v-card-text>
          </wc-data-box>
        </v-col>
       <v-col cols='12' md='3'>
          <wc-data-box
            title='Posting Date'
            titleIcon='mdi-calendar'
            :showEdit='true'
            @edit='edit'
          >
            <div class='headline text-center my-4' v-if='journal_entry.posting_date' >{{ journal_entry.posting_date | longDate }}</div>
          </wc-data-box>
        </v-col>
        <v-col cols='12' md='3' class='text-right'>
          <v-btn
            large
            block
            :elevation='(!are_numeric || !lines_have_accounts || !lines_exist || lines_empty || !balanced) ? 0 : 3'
            :disabled='(!are_numeric || !lines_have_accounts || !lines_exist || lines_empty || !balanced)'
            color='info'
            class='mb-3'
            @click='postEntry'
          >
            <v-icon left>mdi-stamper</v-icon>
            Post Invoice
          </v-btn>
        </v-col>
       </v-row>
      <v-row>
        <v-col cols='12'>
          <wc-data-box title='Lines' :showAdd='true' @add='addLine'>
            <v-simple-table dense>
              <thead>
                <tr class='overline'>
                  <th>Account</th>
                  <th>Description</th>
                  <th width='120' class='text-right'>Debit</th>
                  <th width='120' class='text-right'>Credit</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for='(line, index) in lines' :key='index'>
                  <td>
                    <v-form>
                      <v-autocomplete
                        :items='accounts'
                        item-value='id'
                        item-text='name_number'
                        class='body-2'
                        single-line
                        v-model='line.account'
                        v-on:input='canPost'
                        :rules='[rules.required]'
                        dense
                        hide-details
                      >
                        <template v-slot:item='data'>
                          <account-picker-list-item :account='data.item' v-on:input='canPost'></account-picker-list-item>
                        </template>
                      </v-autocomplete>
                    </v-form>
                  </td>
                  <td>
                    <v-text-field
                      v-model='line.description'
                      class='body-2'
                      dense
                      hide-details
                      single-line
                      v-on:input='canPost'
                    ></v-text-field>
                  </td>
                  <td class='text-right'>
                    <v-text-field
                      v-model='line.debit'
                      :rules='[rules.decimalOrEmpty]'
                      class='body-2'
                      reverse
                      dense
                      hide-details
                      single-line
                      v-on:input='canPost'
                    ></v-text-field>
                  </td>
                  <td class='text-right'>
                    <v-text-field
                      v-model='line.credit'
                      :rules='[rules.decimalOrEmpty]'
                      class='body-2'
                      reverse
                      dense
                      hide-details
                      single-line
                      v-on:input='canPost'
                    ></v-text-field>
                  </td>
                  <td class='text-right'>
                    <wc-line-actions
                      :hideEdit='true'
                      :hideSortUp='true'
                      :hideSortDown='true'
                      @deleteLine='deleteLine(index)'
                    >
                    </wc-line-actions>
                  </td>
                </tr>
              </tbody>
              <tbody>
                <tr class='overline grey lighten-4'>
                  <td></td>
                  <td></td>
                  <td width='120' class='text-right'>{{ totals.debits }}</td>
                  <td width='120' class='text-right'>{{ totals.credits }}</td>
                  <td></td>
                </tr>
                <tr class='overline error font-weight-bold white--text' v-if='!balanced'>
                  <td></td>
                  <td></td>
                  <td width='240' colspan='2' class='text-right'>NOT BALANCED</td>
                  <td></td>
                </tr>
                <tr class='overline error font-weight-bold white--text' v-if='!linesHaveAccounts'>
                  <td></td>
                  <td></td>
                  <td width='240' colspan='2' class='text-right'>ACCOUNT MISSING</td>
                  <td></td>
                </tr>
                <tr class='overline error font-weight-bold white--text' v-if='!areNumeric'>
                  <td></td>
                  <td></td>
                  <td width='240' colspan='2' class='text-right'>ENTER A VALID NUMBER</td>
                  <td></td>
                </tr>
              </tbody>
            </v-simple-table>
          </wc-data-box>
        </v-col>
      </v-row>
    </v-container>

    <wc-dialog
      width='450'
      :showDialog='showDialog'
      :title='$store.state.icons.dataBox.details.title'
      :icon='$store.state.icons.dataBox.details.icon'
      saveText='Update'
      @cancel='showDialog = false'
      @save='updateDetails'
    >
      <v-row dense v-if='journal_entry' :journal_entry='journal_entry'>
        <v-col cols='12'>
          <v-textarea
            outlined
            label='Description'
            v-model='journal_entry.description'
          ></v-textarea>
        </v-col>
        <v-col cols='6'>
          <wc-datepicker
            label='Posting date'
            v-model='journal_entry.posting_date'
            required
            :min='this.$store.state.misc.accounting_settings.closed_fiscal_date'
          ></wc-datepicker>
        </v-col>
      </v-row>
    </wc-dialog>
  </div>
</template>

<script>
import accountingAPI from '@/api/accounting';
import AccountPickerListItem from '@/modules/accounting/components/AccountPickerListItem';
import WCPosted from '@/components/chips/WCPosted';
import formRules from '@/mixins/formRules';
import moment from 'moment';

export default {
  name: 'create-journal-entry',
  mixins: [formRules],
  data:()=> ({
    are_numeric: true,
    lines_empty: true,
    lines_exist: false,
    lines_have_accounts: false,
    balanced: true,
    isLoading: false,
    showDialog: false,
    showReversing: false,
    showConfirmReversal: false,
    lines: [],
    accounts: [],
    editMode: false,
    journal_entry: null,
    empty_line: {
      account: null,
      division: null,
      period: null,
      invoice: null,
      expense: null,
      client: null,
      proejct: null,
      description: null,
      debit: 0,
      credit: 0
    }
  }),
  computed: {
    areNumeric(){
      for (let i = 0; i < this.lines.length; i++) {
        const debit = this.lines[i].debit;
        const credit = this.lines[i].credit;
        const debit_result = parseFloat(debit);
        const credit_result = parseFloat(credit);
        if(isNaN(debit_result) || isNaN(credit_result)){
          return false;
        }
      }
      return true;
    },
    linesHaveAccounts(){
      if(this.lines.length === 0){
        return true;
      }
      for (let i = 0; i < this.lines.length; i++) {
        const account_valid = this.lines[i].account !== null && this.lines[i].account !== undefined;
        const debit_has_balance = this.lines[i].debit !== 0 && this.lines[i].debit !== "0";
        const credit_has_balance = this.lines[i].credit !== 0 && this.lines[i].credit !== "0";
        const has_balance = debit_has_balance || credit_has_balance;
        if(account_valid === false && has_balance === true){
          return false;
        }
      }
      return true;
    },
    allLinesEmpty(){
      let lines_empty = true;
      this.lines.map(line => {
        const account_valid = line.account !== null && line.account !== undefined;
        const debit_has_balance = line.debit !== 0 && line.debit !== "0";
        const credit_has_balance = line.credit !== 0 && line.credit !== "0";
        if(account_valid || debit_has_balance || credit_has_balance){
          lines_empty = false;
          return;
        }
      });
      return lines_empty;
    },
    isBalanced(){
      return this.totals.debits === this.totals.credits;
    },
    totals(){
      return this.getTotals();
    }
  },
  mounted(){
    console.log("create");
    this.getData()
  },
  methods: {
    createEmptyJournalEntry(){
      const journal_entry = {};
      journal_entry.posting_date = moment().format('YYYY-MM-DD').toString();
      journal_entry.description = '';
      journal_entry.created_by = this.$store.state.auth.profile.id;
      journal_entry.correcting_entry = false;
      journal_entry.reversing_entry = false;
      this.journal_entry = journal_entry;
    },
    getDescription(){
      return this.description;
    },
    getPostingDate(){
      return this.posting_date;
    },
    canPost(){
      if(this.checkIfLinesExist() === false){
        this.lines_exist = false;
      }else{
        this.lines_exist = true;
      }

      if(this.areNumeric === false){
        this.are_numeric = false;
      }else{
        this.are_numeric = true;
      }

      if(this.linesHaveAccounts === false){
        this.lines_have_accounts = false;
      }else{
        this.lines_have_accounts = true;
      }

      if(this.isBalanced === false){
        this.balanced = false;
      }else{
        this.balanced = true;
      }

      if(this.allLinesEmpty === true){
        this.lines_empty = true;
      }else{
        this.lines_empty = false;
      }
    },
     checkIfLinesExist(){
      if(this.lines.length > 0){
        return true;
      }
      return false;
    },
    async getData(){
      this.accounts = await accountingAPI.getActiveAccountsPicker();
      this.createEmptyJournalEntry();
    },
    edit(){
      this.showDialog = true;
    },
    async deleteLine (index) {
      this.lines.splice(index, 1);
      this.getTotals();
      this.canPost();
      
    },
    updateDetails(){
      this.showDialog = false;
    },
    getTotals(){
      let debits = 0;
      let credits = 0;

      let hasErr = false

      for (let i = 0; i < this.lines.length; i++) {
        if (isNaN(this.lines[i].debit) || isNaN(this.lines[i].credit)) {
          hasErr = true
        }
        debits += !this.lines[i].debit ? 0 : parseFloat(this.lines[i].debit)
        credits += !this.lines[i].credit ? 0 : parseFloat(this.lines[i].credit)
      }
      if (hasErr) {
        return { debits: 'ERR', credits: 'ERR' }
      }
      return {
        debits: this.$options.filters.reportAmount(debits),
        credits: this.$options.filters.reportAmount(credits)
      }
    },
    addLine(){
      this.lines.push(JSON.parse(JSON.stringify(this.empty_line)));
      this.canPost();
    },
    async postEntry(){
      this.isLoading = true;
      if(this.lines.length === 0){
        return;
      }
      this.journal_entry = await accountingAPI.createJournalEntry(this.journal_entry);
      for (let i = 0; i < this.lines.length; i++) {
        this.lines[i].journal_entry = this.journal_entry.id;
        if(this.lines[i].account === null || this.lines[i].account === undefined){
          continue;
        }
        await accountingAPI.createJournalEntryLineItem(this.lines[i]);
      }
      this.$router.push({ name: 'JournalEntry', params: { id: this.journal_entry.id } })
    }
  },
  components: {
    'wc-posted': WCPosted,
    'account-picker-list-item': AccountPickerListItem
  }
}
</script>
